import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";
import type { InboxComment } from "./use-inbox-comments";

function invalidate(qc: ReturnType<typeof useQueryClient>, brandId: string) {
  qc.invalidateQueries({ queryKey: ["inbox-comments", brandId] });
  qc.invalidateQueries({ queryKey: ["inbox-comment-replies", brandId] });
}

export function useReplyToComment(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, text }: { commentId: string; text: string }) =>
      apiFetch<{ ok: true; zernioReplyId: string | null }>(
        `/brands/${brandId}/inbox/comments/${commentId}/reply`,
        { method: "POST", body: JSON.stringify({ text }) },
      ),
    onSuccess: () => invalidate(qc, brandId),
  });
}

export function usePrivateReplyToComment(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, text }: { commentId: string; text: string }) =>
      apiFetch<{ ok: true }>(
        `/brands/${brandId}/inbox/comments/${commentId}/private-reply`,
        { method: "POST", body: JSON.stringify({ text }) },
      ),
    onSuccess: () => invalidate(qc, brandId),
  });
}

export function useHideComment(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, hide }: { commentId: string; hide: boolean }) =>
      apiFetch<InboxComment>(
        `/brands/${brandId}/inbox/comments/${commentId}/${hide ? "hide" : "unhide"}`,
        { method: "POST", body: JSON.stringify({}) },
      ),
    onSuccess: () => invalidate(qc, brandId),
  });
}

export function useLikeComment(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, liked }: { commentId: string; liked: boolean }) =>
      apiFetch<InboxComment>(
        `/brands/${brandId}/inbox/comments/${commentId}/like`,
        { method: "POST", body: JSON.stringify({ liked }) },
      ),
    onSuccess: () => invalidate(qc, brandId),
  });
}

export function useDeleteComment(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (commentId: string) =>
      apiFetch<InboxComment>(
        `/brands/${brandId}/inbox/comments/${commentId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => invalidate(qc, brandId),
  });
}

export function useAssignComment(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, userId }: { commentId: string; userId: string | null }) =>
      apiFetch<InboxComment>(
        `/brands/${brandId}/inbox/comments/${commentId}/assign`,
        { method: "POST", body: JSON.stringify({ userId }) },
      ),
    onSuccess: () => invalidate(qc, brandId),
  });
}

export function useSetCommentStatus(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      commentId,
      status,
    }: {
      commentId: string;
      status: "OPEN" | "SNOOZED" | "DONE";
    }) =>
      apiFetch<InboxComment>(
        `/brands/${brandId}/inbox/comments/${commentId}/status`,
        { method: "POST", body: JSON.stringify({ status }) },
      ),
    onSuccess: () => invalidate(qc, brandId),
  });
}

/** TikTok for Business only: pin/unpin a top-level comment on our post. */
export function usePinComment(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ commentId, pinned }: { commentId: string; pinned: boolean }) =>
      apiFetch<InboxComment>(
        `/brands/${brandId}/inbox/comments/${commentId}/pin`,
        { method: "POST", body: JSON.stringify({ pinned }) },
      ),
    onSuccess: () => invalidate(qc, brandId),
  });
}

export interface CommentReply {
  id: string;
  text: string | null;
  authorName: string | null;
  authorUsername: string | null;
  authorAvatar: string | null;
  postedAt: string | null;
  likeCount: number;
  /** Our own reply (outbound). */
  fromOwnAccount: boolean;
}

/** A comment's reply thread, fetched live when the thread is expanded. */
export function useCommentReplies(brandId: string, commentId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["inbox-comment-replies", brandId, commentId],
    queryFn: () =>
      apiFetch<{ replies: CommentReply[]; nextCursor: string | null }>(
        `/brands/${brandId}/inbox/comments/${commentId}/replies`,
      ),
    enabled: enabled && !!brandId && !!commentId,
    staleTime: 60_000,
    retry: false,
  });
}
